import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChunkLoadBoundary, Logo, PageSkeleton, ToastProvider, type PageSkeletonVariant } from '@mbbe/ui';
import { DestekAuthProvider, useDestekAuth } from './DestekAuthContext';

const Login = React.lazy(() => import('./pages/Login'));
const IssueList = React.lazy(() => import('./pages/IssueList'));
const NewIssue = React.lazy(() => import('./pages/NewIssue'));
const IssueDetail = React.lazy(() => import('./pages/IssueDetail'));

import { useLanguage } from '../contexts/LanguageContext';
import { SupportSurfaceProvider } from './SupportSurfaceContext';

export function Protected({ children }: { children: React.ReactNode }) {
  const { user, isCustomer, isLoading } = useDestekAuth();
  if (isLoading) {
    return <DestekRouteLoading forceShell />;
  }
  if (!user || !isCustomer) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function resolveDestekSkeletonVariant(pathname: string): PageSkeletonVariant {
  if (pathname === '/login' || pathname === '/yeni') return 'form';
  if (pathname.startsWith('/issue/')) return 'detail';
  return 'list';
}

function DestekLoadingShell({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage();
  return (
    <div
      data-loading-shell="standalone-support"
      className="relative min-h-screen overflow-x-hidden bg-dark-900 text-white"
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(60rem_40rem_at_80%_-10%,rgba(59,130,246,0.10),transparent_60%),radial-gradient(50rem_30rem_at_-10%_10%,rgba(37,99,235,0.08),transparent_55%)]"
      />
      <header className="relative border-b border-white/5 bg-dark-900/80">
        <div className="mx-auto flex h-16 max-w-4xl items-center px-4">
          <div className="flex items-center gap-2.5" aria-label={`mbbe ${t('destek_support')}`}>
            <Logo variant="contained" className="h-8 w-8" />
            <span className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold tracking-tight">mbbe</span>
              <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-brand-300">
                {t('destek_support')}
              </span>
            </span>
          </div>
        </div>
      </header>
      <main className="relative mx-auto max-w-4xl px-4 py-8">{children}</main>
    </div>
  );
}

export function DestekRouteLoading({ forceShell = false }: { forceShell?: boolean }) {
  const { pathname } = useLocation();
  const { language, t } = useLanguage();
  const isLogin = pathname === '/login';
  const skeleton = (
    <PageSkeleton
      variant={resolveDestekSkeletonVariant(pathname)}
      label={
        isLogin
          ? language === 'tr' ? 'Destek girişi hazırlanıyor' : 'Preparing support sign in'
          : language === 'tr' ? 'Destek alanı yükleniyor' : 'Loading support workspace'
      }
    />
  );

  if (forceShell || !isLogin) return <DestekLoadingShell>{skeleton}</DestekLoadingShell>;

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-900 px-4 py-12 text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(50rem_35rem_at_50%_-10%,rgba(59,130,246,0.14),transparent_60%)]" />
      <div className="relative w-full max-w-3xl">
        <div className="mb-8 flex items-center justify-center gap-3">
          <Logo variant="contained" className="h-11 w-11" />
          <div>
            <p className="font-bold text-white">mbbe</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-brand-300">{t('destek_support')}</p>
          </div>
        </div>
        {skeleton}
      </div>
    </main>
  );
}

export default function DestekApp() {
  return (
    <ToastProvider>
      <DestekAuthProvider>
        <BrowserRouter>
          <ChunkLoadBoundary>
            <React.Suspense fallback={<DestekRouteLoading />}>
              <SupportSurfaceProvider surface="standalone">
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Protected><IssueList /></Protected>} />
                  <Route path="/yeni" element={<Protected><NewIssue /></Protected>} />
                  <Route path="/issue/:id" element={<Protected><IssueDetail /></Protected>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </SupportSurfaceProvider>
            </React.Suspense>
          </ChunkLoadBoundary>
        </BrowserRouter>
      </DestekAuthProvider>
    </ToastProvider>
  );
}
